import styled from "styled-components";
import dayjs from "dayjs";
import { useMemo } from "react";

import { Icon } from "components/atoms";

import { ReportTableItemWithTransfer } from "interfaces/reports";

import { IconName } from "interfaces/icons";

import { getChannelName } from "utils/channels";

import { MAIN_THEME } from "styles/colors";

const TableRow = styled.div<{ columns: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns};
	gap: 0;
	cursor: pointer;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
	transition: background 0.2s ease;

	&:hover {
		background-color: ${MAIN_THEME.TABLE_HOVER};
	}
`;

const InfoText = styled.p<{ color?: string }>`
	color: ${(props) => props.color || MAIN_THEME.TEXT_PRIMARY};
	display: flex;
	align-items: center;
	font-size: 13px;
	justify-content: flex-start;
	text-align: left;
	height: 52px;
	background: transparent;
	border: none;
	padding: 10px 8px;
	margin: 0;

	@media (max-width: 768px) {
		font-size: 11px;
		height: 44px;
		padding: 8px 6px;
	}
`;

const StatusBadge = styled.div<{ color: string; isSecondary?: boolean }>`
	padding: 4px 12px;
	border-radius: 99px;
	font-size: 11px;
	font-weight: 600;
	text-transform: lowercase;
	letter-spacing: 0.5px;
	background: ${({ color, isSecondary }) => (isSecondary ? "rgba(148, 163, 184, 0.15)" : `${color}25`)};
	color: ${({ color, isSecondary }) => (isSecondary ? MAIN_THEME.TEXT_PRIMARY : color)};
	border: 1px solid ${({ color, isSecondary }) => (isSecondary ? "rgba(148, 163, 184, 0.25)" : `${color}30`)};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 80px;
`;

interface Props {
	data: ReportTableItemWithTransfer;
	showProperty?: boolean;
	onSelect: (item: ReportTableItemWithTransfer | null) => void;
	visibleColumnIds?: string[];
	gridTemplateColumns?: string;
}

const PropertyReportDataItem = (props: Props): JSX.Element => {
	const { data, showProperty, onSelect, visibleColumnIds, gridTemplateColumns } = props;
	const {
		start_date,
		end_date,
		guest,
		currency,
		accommodation,
		host_service_fee,
		cleaning,
		payout_price,
		net_accomdation,
		hotelise_help,
		owner_income,
		channel,
		code,
		status,
	} = data._item;
	const { _accountHolder, _toTransfer } = data._transferDetails;

	const channelName = useMemo(() => {
		const name = getChannelName(channel).toLowerCase();
		if (name === "airbnb-official") return "airbnb";
		return name;
	}, [channel]);

	const renderAccountHolderIcon = () =>
		_accountHolder === 1 ? (
			<Icon name={IconName.LOGO} size={18} color={MAIN_THEME.SECONDARY} />
		) : (
			<Icon name={IconName.USER} size={20} color={MAIN_THEME.SECONDARY} />
		);

	const name = useMemo(() => {
		const nameWithoutDash = (data.property?.name || "").replaceAll("-", " ");
		const nameWithoutUnderscore = nameWithoutDash.replaceAll("_", " ");

		return (
			nameWithoutUnderscore.charAt(0).toUpperCase() +
			nameWithoutUnderscore.slice(1)
		);
	}, [data]);

	const renderStatus = () => {
		const lowerStatus = (status || "n/a").toLowerCase();
		const isAccepted = lowerStatus === "accepted";

		return (
			<InfoText>
				<StatusBadge 
					color={isAccepted ? MAIN_THEME.TEXT_SECONDARY : MAIN_THEME.CANCEL_COLOR} 
					isSecondary={isAccepted}
				>
					{lowerStatus}
				</StatusBadge>
			</InfoText>
		);
	};

	const renderChannel = () => {
		return (
			<InfoText>
				<StatusBadge color={MAIN_THEME.TEXT_SECONDARY} isSecondary>{channelName}</StatusBadge>
			</InfoText>
		);
	};

	const isVisible = (id: string) => {
		if (!visibleColumnIds) return true; // Default behavior if not provided
		return visibleColumnIds.includes(id);
	}

	const defaultGridColumns = showProperty
		? "1fr 1fr 1fr 0.5fr 1fr 0.75fr 0.75fr 0.65fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 0.5fr"
		: "1fr 1fr 0.5fr 1fr 0.75fr 0.75fr 0.65fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 0.5fr";

	return (
		<TableRow
			onClick={() => onSelect(data)}
			columns={gridTemplateColumns || defaultGridColumns}
		>
			{showProperty && isVisible('property') ? (
				<InfoText
					style={{
						textTransform: "capitalize",
					}}
				>
					{name}
					</InfoText>
				) : null}
				{isVisible('channel') && renderChannel()}
				{isVisible('code') && <InfoText>{code}</InfoText>}

			{isVisible('status') && renderStatus()}
			{isVisible('fullName') && <InfoText>
				{guest.first_name} {guest.last_name}
			</InfoText>}
			{isVisible('checkin') && <InfoText>{dayjs(start_date).format("D MMM, YYYY")}</InfoText>}
			{isVisible('checkout') && <InfoText>{dayjs(end_date).format("D MMM, YYYY")}</InfoText>}

			{isVisible('currency') && <InfoText>{currency}</InfoText>}
			{isVisible('accommodation') && <InfoText>{accommodation}</InfoText>}
			{isVisible('channelFee') && <InfoText>{host_service_fee}</InfoText>}
			{isVisible('netAccommodation') && <InfoText>{net_accomdation}</InfoText>}
			{isVisible('cleaning') && <InfoText>{cleaning}</InfoText>}
			{isVisible('payout') && <InfoText>{payout_price}</InfoText>}
			{isVisible('hoteliseHelp') && <InfoText>{hotelise_help}</InfoText>}
			{isVisible('ownerIncome') && <InfoText>{owner_income}</InfoText>}
			{isVisible('transfer') && <InfoText>{_toTransfer}</InfoText>}
			{isVisible('sender') && <InfoText>{renderAccountHolderIcon()}</InfoText>}
		</TableRow>
	);
};

export default PropertyReportDataItem;
